import { AppState } from '../../store';
import { createSelector } from '@reduxjs/toolkit';
import { DocumentType } from '@bus/models';
import { ContractDocument, ContractDocumentCategory, CustomData } from '../../../models/data-storage.model';
import { EXCLUDED_CATEGORY_CODES } from '../../../pages/upload-page/utils';

export const getSession = (state: AppState) => state.appData.session;
export const getTransactionStatus = (state: AppState) => state.appData.dataStorage?.data?.transaction?.status || null;
export const getBrand = (state: AppState) => state.appData.dataStorage?.brand || null;
export const getCustomerFirstName = (state: AppState) =>
  state.appData.dataStorage?.data?.customerData?.firstName || null;

export const getIsDataStorageDataLoading = (state: AppState) => state.appData.ui.dataLoadingCounter > 0;
export const getIsUploadPending = (state: AppState) => state.appData.ui.fileUploadPendingCounter > 0;
export const getDidUploadFail = (state: AppState) => state.appData.ui.didUploadFail;
export const getIsUploadPageLoaded = (state: AppState) => state.appData.ui.isUploadPageLoaded;
export const getIsAcknowledgementPageLoaded = (state: AppState) => state.appData.ui.isAcknowledgementPageLoaded;

export const getDataStorageData = (state: AppState) => state.appData.dataStorage;

export const doesRequestInfoTypeRequireDocsUpload = (requestInfoType: CustomData['requestInfoType'] | undefined) =>
  requestInfoType === 'IDDOCS';

export const getRequestInfoType = (state: AppState) => getDataStorageData(state)?.data?.custom?.requestInfoType;

export const getIsDocumentsUploadNeeded = (state: AppState) =>
  doesRequestInfoTypeRequireDocsUpload(getRequestInfoType(state));

export const getUserUploadedFiles = createSelector(getDataStorageData, dataStorageData => {
  return (
    dataStorageData?.data?.financialProduct?.contracts?.[0]?.documents.filter(
      document => (document as ContractDocument).documentType === DocumentType.UPLOADS
    ) || []
  );
});

export const getDocumentsToBeUploaded = createSelector(getDataStorageData, dataStorageData => {
  return (
    dataStorageData?.data?.financialProduct?.contracts?.[0]?.documents.filter(document => {
      return (
        Boolean(document.customCategoryCode) &&
        !EXCLUDED_CATEGORY_CODES.includes(document.customCategoryCode) &&
        (!document.fileId || document.documentType === DocumentType.UPLOADS)
      );
    }) || []
  );
});

export const getDocumentsToBeUploadedCategories = createSelector(getDocumentsToBeUploaded, documents => {
  return documents.reduce((acc: ContractDocumentCategory[], document) => {
    if (
      !EXCLUDED_CATEGORY_CODES.includes(document.customCategoryCode) &&
      !acc.find(category => category.customCategoryCode === document.customCategoryCode)
    ) {
      acc.push({
        customCategoryCode: document.customCategoryCode,
        customCategoryName: document.customCategoryName,
        customCategoryDescription: document.customCategoryDescription,
        customCategoryDisplayLabel: document.customCategoryDisplayLabel,
        customCategorySupplementaryText: document.customCategorySupplementaryText
      });
    }
    return acc;
  }, []);
});

export const getShouldStepsWizardBeHidden = createSelector(
  getIsDocumentsUploadNeeded,
  isDocumentsUploadNeeded => !isDocumentsUploadNeeded
);
